export const METRIC_KEYS = ['accuracy', 'memory', 'speed', 'consistency', 'control'] as const;
export type MetricKey = typeof METRIC_KEYS[number];
export type MetricScores = Record<MetricKey, number>;

const LABELS_KO: Record<MetricKey, string> = {
  accuracy: '정확도',
  memory: '기억하기',
  speed: '반응 속도',
  consistency: '집중 유지',
  control: '실수 줄이기',
};

const LABELS_EN: Record<MetricKey, string> = {
  accuracy: 'Accuracy',
  memory: 'Working Memory',
  speed: 'Response Speed',
  consistency: 'Sustained Attention',
  control: 'Error Control',
};

export function getCognitiveLabels(language: string): Record<MetricKey, string> {
  return language === 'en' ? LABELS_EN : LABELS_KO;
}

type Band = 'high' | 'mid' | 'low';

const PROFESSIONAL_PHRASES: Record<MetricKey, Record<Band, string[]>> = {
  accuracy: {
    high: ['제시된 규칙을 안정적으로 유지했습니다', '순서 정보를 정확하게 재현했습니다', '난이도 변화에서도 정확성이 유지되었습니다', '시각 단서를 일관되게 구분했습니다', '정확한 반응 선택이 두드러졌습니다'],
    mid: ['기본 순서의 재현은 안정적이었습니다', '대부분의 규칙을 정확하게 적용했습니다', '짧은 순서에서는 정확한 수행을 보였습니다', '정확도는 기대 범위의 양상을 보였습니다', '문항 난이도에 따라 정확도 변화가 관찰되었습니다'],
    low: ['긴 순서에서 순서 재현의 부담이 관찰되었습니다', '난이도가 올라갈 때 선택 오류가 늘어났습니다', '시각 정보를 유지하는 과정에서 부담이 나타났습니다', '정확한 순서 확인에 더 많은 시간이 필요했습니다', '복잡한 문항에서 반응 선택의 어려움이 관찰되었습니다'],
  },
  memory: {
    high: ['여러 색상 정보를 유지하는 폭이 넓었습니다', '순서 단서를 기억하는 수행이 안정적이었습니다', '길어진 배열에서도 작업기억을 잘 유지했습니다', '연속 정보를 묶어 기억하는 전략이 효과적이었습니다', '기억 폭 확장에 잘 적응했습니다'],
    mid: ['일상적인 길이의 순서는 안정적으로 유지했습니다', '작업기억 폭이 현재 단계에 적절했습니다', '짧은 배열의 기억 유지가 비교적 원활했습니다', '순서가 길어질수록 부담이 점진적으로 나타났습니다', '기억 폭은 중간 수준의 일관성을 보였습니다'],
    low: ['배열 길이가 늘어날 때 기억 유지 부담이 나타났습니다', '후반 순서 정보를 유지하는 데 어려움이 관찰되었습니다', '여러 단서를 동시에 보관하는 과정이 부담스러웠습니다', '긴 배열에서 순서 바뀜이 주로 나타났습니다', '기억 폭을 확장하는 과정에서 보조 단서가 필요했습니다'],
  },
  speed: {
    high: ['기억 단계 이후 반응 개시가 민첩했습니다', '정확성을 유지하며 빠르게 반응했습니다', '첫 반응과 연속 반응의 속도가 안정적이었습니다', '시각 정보에서 운동 반응으로의 전환이 원활했습니다', '반응 준비 시간이 짧고 일정했습니다'],
    mid: ['반응 개시 시간이 전반적으로 안정적이었습니다', '속도와 정확성 사이의 균형이 유지되었습니다', '문항 난이도에 맞는 반응 속도를 보였습니다', '첫 반응의 속도는 기대 범위였습니다', '연속 입력에서 무리 없는 속도를 유지했습니다'],
    low: ['기억 단계 뒤 반응을 시작하는 시간이 길어졌습니다', '난이도가 높아질 때 반응 준비 시간이 늘어났습니다', '정확한 선택을 위해 충분한 반응 시간이 필요했습니다', '시각 단서에서 입력으로 전환하는 과정이 느려졌습니다', '연속 반응의 속도 저하가 관찰되었습니다'],
  },
  consistency: {
    high: ['문항 전반에서 반응 간격이 고르게 유지되었습니다', '난이도 변화에도 수행 리듬이 안정적이었습니다', '반응시간 변동이 작아 집중 유지가 돋보였습니다', '긴 과제에서도 일정한 반응 패턴을 보였습니다', '수행 속도의 흔들림이 적었습니다'],
    mid: ['전반적으로 무난한 반응 리듬을 유지했습니다', '일부 문항에서만 반응 간격 변화가 나타났습니다', '수행 흐름은 대체로 안정적이었습니다', '난이도 변화에 따른 속도 조절이 관찰되었습니다', '중간 수준의 반응 일관성을 보였습니다'],
    low: ['문항 사이 반응 간격의 변동이 크게 나타났습니다', '긴 순서에서 수행 리듬이 흔들리는 양상이 보였습니다', '일부 문항에서 멈춤 시간이 길어졌습니다', '집중 유지에 따른 반응 편차가 관찰되었습니다', '난이도 변화 시 반응 속도의 기복이 나타났습니다'],
  },
  control: {
    high: ['불필요한 추가 입력 없이 규칙을 잘 지켰습니다', '순서 오류를 효과적으로 통제했습니다', '충동적인 반응을 억제하며 정확히 입력했습니다', '오류 후에도 다음 문항의 규칙을 안정적으로 적용했습니다', '선택·순서 오류가 적었습니다'],
    mid: ['오류 통제는 전반적으로 기대 범위였습니다', '일부 복잡한 문항에서만 순서 오류가 나타났습니다', '대부분의 문항에서 불필요한 입력을 피했습니다', '규칙을 유지하려는 반응 조절이 관찰되었습니다', '오류 빈도는 중간 수준이었습니다'],
    low: ['복잡한 배열에서 순서 바꿈 오류가 집중되었습니다', '예상하지 않은 추가 입력이 일부 관찰되었습니다', '빠른 반응 상황에서 선택 오류가 늘어났습니다', '오류 뒤 다음 문항의 규칙 전환에 부담이 나타났습니다', '입력 전 순서 확인이 더 필요한 양상이었습니다'],
  },
};

const OPENERS = [
  '이번 수행에서는', '이번 회차의 수행 양상은', '현재 기록에서는', '문항별 반응을 종합하면',
  '이번 학습 결과는', '수행 로그를 기준으로 보면', '난이도 변화에 따른 반응은',
  '이번 활동의 반응 패턴은', '기록된 입력 흐름은', '이번 회차의 인지 프로필은',
];
const CONNECTORS = ['동시에', '또한', '그리고', '반면', '특히', '아울러', '한편', '복합적으로', '전반적으로', '세부적으로'];
const CLOSERS = [
  '이번 기록은 과제 중 나타난 반응 특성을 중심으로 정리했습니다.',
  '해석은 이번 활동의 입력 기록을 기반으로 하며, 다른 인지 영역을 포괄하지 않습니다.',
  '동일 과제를 반복할 때에는 연습 효과와 컨디션 변화를 함께 고려해야 합니다.',
  '이번 결과는 과제 수행 중 기록된 반응 특성을 중심으로 해석했습니다.',
  '개인의 수면, 피로, 입력 기기 경험에 따라 반응은 달라질 수 있습니다.',
];

// Integrated (multi-session) reports only have per-session averages, not per-item
// difficulty data, so this phrase set avoids asserting a difficulty-driven trend
// (e.g. "정확도가 난이도 상승에 따라 하락") and instead describes relative standing
// among the five metrics across the recorded sessions.
const INTEGRATED_PHRASES: Record<MetricKey, Record<Band, string[]>> = {
  accuracy: {
    high: ['여러 회차에 걸쳐 정답 선택이 안정적으로 유지되었습니다', '누적 기록에서 정확한 반응이 꾸준히 나타났습니다', '반복된 학습에서도 정확도가 높은 수준으로 유지되었습니다'],
    mid: ['누적 기록에서 정확도는 무난한 수준을 보였습니다', '회차별로 정확도가 대체로 안정적이었습니다', '정확도는 다른 지표 대비 중간 수준으로 나타났습니다'],
    low: ['다른 지표에 비해 정확도가 상대적으로 낮게 나타났습니다', '누적 기록에서 정확도 향상의 여지가 관찰되었습니다', '정확도는 이번 누적 기록에서 보완이 필요한 영역으로 나타났습니다'],
  },
  memory: {
    high: ['여러 회차에 걸쳐 기억 폭이 안정적으로 유지되었습니다', '누적 기록에서 작업기억 수행이 꾸준히 높게 나타났습니다', '반복 학습에서도 기억 유지력이 우수했습니다'],
    mid: ['누적 기록에서 기억하기 지표는 무난한 수준을 보였습니다', '작업기억 수행이 회차별로 대체로 안정적이었습니다', '기억하기는 다른 지표 대비 중간 수준으로 나타났습니다'],
    low: ['다른 지표에 비해 기억하기가 상대적으로 낮게 나타났습니다', '누적 기록에서 작업기억 향상의 여지가 관찰되었습니다', '기억하기는 이번 누적 기록에서 보완이 필요한 영역으로 나타났습니다'],
  },
  speed: {
    high: ['여러 회차에 걸쳐 반응 속도가 안정적으로 빨랐습니다', '누적 기록에서 반응 개시가 꾸준히 민첩했습니다', '반복 학습에서도 반응 속도가 우수하게 유지되었습니다'],
    mid: ['누적 기록에서 반응 속도는 무난한 수준을 보였습니다', '반응 속도가 회차별로 대체로 안정적이었습니다', '반응 속도는 다른 지표 대비 중간 수준으로 나타났습니다'],
    low: ['다른 지표에 비해 반응 속도가 상대적으로 느리게 나타났습니다', '누적 기록에서 반응 속도 향상의 여지가 관찰되었습니다', '반응 속도는 이번 누적 기록에서 보완이 필요한 영역으로 나타났습니다'],
  },
  consistency: {
    high: ['여러 회차에 걸쳐 집중 유지가 안정적으로 나타났습니다', '누적 기록에서 반응 리듬이 꾸준히 일정했습니다', '반복 학습에서도 집중력이 우수하게 유지되었습니다'],
    mid: ['누적 기록에서 집중 유지는 무난한 수준을 보였습니다', '반응 리듬이 회차별로 대체로 안정적이었습니다', '집중 유지는 다른 지표 대비 중간 수준으로 나타났습니다'],
    low: ['다른 지표에 비해 집중 유지가 상대적으로 낮게 나타났습니다', '누적 기록에서 집중 유지 향상의 여지가 관찰되었습니다', '집중 유지는 이번 누적 기록에서 보완이 필요한 영역으로 나타났습니다'],
  },
  control: {
    high: ['여러 회차에 걸쳐 오류 통제가 안정적으로 유지되었습니다', '누적 기록에서 불필요한 입력이 꾸준히 적었습니다', '반복 학습에서도 오류 통제가 우수하게 유지되었습니다'],
    mid: ['누적 기록에서 실수 줄이기는 무난한 수준을 보였습니다', '오류 통제가 회차별로 대체로 안정적이었습니다', '실수 줄이기는 다른 지표 대비 중간 수준으로 나타났습니다'],
    low: ['다른 지표에 비해 실수 줄이기가 상대적으로 낮게 나타났습니다', '누적 기록에서 오류 통제 향상의 여지가 관찰되었습니다', '실수 줄이기는 이번 누적 기록에서 보완이 필요한 영역으로 나타났습니다'],
  },
};

const INTEGRATED_OPENERS = [
  '이번 누적 기록에서는', '여러 회차의 수행을 종합하면', '누적된 학습 기록을 기준으로 보면',
  '지금까지의 회차를 종합하면', '누적 수행 로그를 기준으로 보면',
];
const INTEGRATED_CLOSERS = [
  '이 소견은 누적된 회차의 평균 지표를 기반으로 하며, 문항별 난이도-정확도 관계를 분석한 결과는 아닙니다.',
  '해석은 여러 회차의 평균 수행을 기반으로 하며, 개별 문항의 난이도 변화는 반영하지 않았습니다.',
  '이번 결과는 누적 회차의 평균 지표를 중심으로 정리했으며, 회차 간 세부 난이도 차이는 고려하지 않았습니다.',
];

const PROFESSIONAL_SUMMARY_EN: Record<MetricKey, Record<Band, string>> = {
  accuracy: {
    high: 'target selection and rule application were consistently accurate',
    mid: 'accuracy was generally stable, with some variation as task demand increased',
    low: 'selection accuracy declined as the task became more demanding',
  },
  memory: {
    high: 'sequential information was retained effectively across the activity',
    mid: 'short sequences were retained reliably, while longer sequences created additional load',
    low: 'retaining and reproducing longer sequences presented a clear challenge',
  },
  speed: {
    high: 'response initiation was prompt while accuracy was maintained',
    mid: 'response speed remained balanced with task accuracy',
    low: 'additional preparation time was needed before responding',
  },
  consistency: {
    high: 'response timing remained stable across items',
    mid: 'performance pace was mostly stable with occasional variation',
    low: 'response timing varied considerably between items',
  },
  control: {
    high: 'unnecessary and impulsive responses were well controlled',
    mid: 'error control was generally maintained, with occasional lapses on complex items',
    low: 'extra or repeated responses increased under higher task demand',
  },
};

// English counterpart of INTEGRATED_PHRASES — describes relative standing across
// recorded sessions instead of a difficulty-driven trend that averaged, session-level
// scores cannot establish.
const PROFESSIONAL_SUMMARY_INTEGRATED_EN: Record<MetricKey, Record<Band, string>> = {
  accuracy: {
    high: 'accuracy remained consistently high across recorded sessions',
    mid: 'accuracy stayed at a moderate, fairly stable level across sessions',
    low: 'accuracy was comparatively lower than the other tracked areas across sessions',
  },
  memory: {
    high: 'working memory performance stayed strong across recorded sessions',
    mid: 'working memory stayed at a moderate, fairly stable level across sessions',
    low: 'working memory was comparatively lower than the other tracked areas across sessions',
  },
  speed: {
    high: 'response speed remained consistently quick across recorded sessions',
    mid: 'response speed stayed at a moderate, fairly stable level across sessions',
    low: 'response speed was comparatively slower than the other tracked areas across sessions',
  },
  consistency: {
    high: 'sustained attention remained stable across recorded sessions',
    mid: 'sustained attention stayed at a moderate, fairly stable level across sessions',
    low: 'sustained attention was comparatively lower than the other tracked areas across sessions',
  },
  control: {
    high: 'error control remained consistently strong across recorded sessions',
    mid: 'error control stayed at a moderate, fairly stable level across sessions',
    low: 'error control was comparatively lower than the other tracked areas across sessions',
  },
};

const EASY_FEEDBACK: Record<Band, { title: string; body: string }> = {
  high: { title: '아주 잘 해냈어요!', body: '색과 순서를 차분히 기억하고, 알맞게 눌렀어요. 지금의 좋은 흐름을 이어가 보세요.' },
  mid: { title: '차근차근 잘 하고 있어요.', body: '쉬운 문제는 잘 풀었고, 조금 긴 문제에서는 천천히 생각할 시간이 더 필요할 수 있어요.' },
  low: { title: '천천히 해도 괜찮아요.', body: '이번 문제는 기억할 것이 많았어요. 색을 한 번 더 살펴보고, 서두르지 않고 눌러 보세요.' },
};
const EASY_STRENGTH: Record<MetricKey, string> = {
  accuracy: '색과 순서를 잘 살펴봤어요.',
  memory: '보았던 정보를 기억하려고 집중했어요.',
  speed: '준비한 뒤 알맞은 때에 반응했어요.',
  consistency: '문제마다 꾸준히 집중했어요.',
  control: '서두르지 않고 신중하게 선택했어요.',
};
const EASY_SUPPORT: Record<MetricKey, string> = {
  accuracy: '다음에는 순서를 속으로 한 번 더 떠올려 보세요.',
  memory: '색을 작은 묶음으로 나누어 기억해 보세요.',
  speed: '정답을 떠올렸다면 망설이지 말고 눌러 보세요.',
  consistency: '한 문제씩 숨을 고르고 같은 속도로 해 보세요.',
  control: '누르기 전에 다음 색을 한 번 더 확인해 보세요.',
};
const EASY_FEEDBACK_EN: Record<Band, { title: string; body: string }> = {
  high: { title: 'Excellent work!', body: 'You remembered the colors and their order carefully. Keep up this steady approach.' },
  mid: { title: 'You are making steady progress.', body: 'You handled the shorter items well. Longer items may need a little more thinking time.' },
  low: { title: 'It is okay to take your time.', body: 'There was a lot to remember. Look carefully and respond without rushing.' },
};
const EASY_STRENGTH_EN: Record<MetricKey, string> = {
  accuracy: 'You checked the colors and their order carefully.',
  memory: 'You focused on remembering what you saw.',
  speed: 'You responded at the right time after getting ready.',
  consistency: 'You stayed engaged from one item to the next.',
  control: 'You made careful choices without rushing.',
};
const EASY_SUPPORT_EN: Record<MetricKey, string> = {
  accuracy: 'Next time, picture the order once more before responding.',
  memory: 'Try remembering the colors in small groups.',
  speed: 'Once you know the answer, respond with confidence.',
  consistency: 'Pause briefly and keep the same pace for each item.',
  control: 'Check the next color once more before responding.',
};

function clamp(value: number, min = 0, max = 100) {
  return Math.round(Math.max(min, Math.min(max, value)));
}

function medianOf(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function band(score: number): Band {
  if (score >= 68) return 'high';
  if (score >= 42) return 'mid';
  return 'low';
}

function hash(parts: (string | number)[]) {
  return parts.join('|').split('').reduce((value, char) => (value * 31 + char.charCodeAt(0)) >>> 0, 7);
}

function phraseFor(key: MetricKey, score: number, seed: number, offset: number, phraseTable: Record<MetricKey, Record<Band, string[]>> = PROFESSIONAL_PHRASES) {
  const phrases = phraseTable[key][band(score)];
  return phrases[(seed + offset) % phrases.length];
}

export interface CognitiveTrial {
  completed: boolean;
  sequence: string[];
  correct: boolean;
  errorType: 'sequence' | 'extra' | 'timeout' | 'perseverative' | null;
  responseTimes: number[];
  inputMode: 'touch' | 'midi';
  rule?: string | null;
  isSwitch?: boolean;
}

export interface CognitiveScoreProfile {
  id: string;
  answerLimit: number;
  speedReference: number;
}

export interface ActivityMetricsResult {
  metrics: MetricScores;
  total: number;
  correct: number;
  median: number;
  maxSpan: number;
  errorCount: number;
  validResponseCount: number;
  variabilityRatio: number | null;
  scoreMethod: string;
}

export function calculateActivityMetrics({ trials, profile, activityId = 'color-sequence-drum' }: {
  trials: CognitiveTrial[];
  profile: CognitiveScoreProfile;
  activityId?: string;
}): ActivityMetricsResult {
  const answered = trials.filter((trial) => trial.completed);
  const total = Math.max(answered.length, 1);
  const correct = answered.filter((trial) => trial.correct).length;
  const accuracyRate = (correct / total) * 100;
  const sequenceErrors = answered.filter((trial) => trial.errorType === 'sequence').length;
  const extraErrors = answered.filter((trial) => trial.errorType === 'extra').length;
  const timedOut = answered.filter((trial) => trial.errorType === 'timeout').length;
  const perseverativeErrors = answered.filter((trial) => trial.errorType === 'perseverative').length;
  const maxAllowedResponse = Math.max(1000, profile.answerLimit * 1000);
  const correctResponseTimes = answered
    .filter((trial) => trial.correct)
    .map((trial) => trial.responseTimes?.[0])
    .filter((time): time is number => Number.isFinite(time) && (time as number) >= 150 && (time as number) <= maxAllowedResponse);
  const median = medianOf(correctResponseTimes) ?? maxAllowedResponse;
  const absoluteDeviations = correctResponseTimes.map((time) => Math.abs(time - median));
  const medianAbsoluteDeviation = medianOf(absoluteDeviations) ?? 0;
  const variabilityRatio = correctResponseTimes.length >= 3 ? medianAbsoluteDeviation / Math.max(median, 1) : null;
  const stabilityScore = variabilityRatio === null ? 60 : clamp(100 - Math.min(0.75, variabilityRatio) * 120);
  const responseCompletionRate = ((answered.length - timedOut) / total) * 100;
  const maxSpan = Math.max(0, ...answered.filter((trial) => trial.correct).map((trial) => trial.sequence.length));
  const maxPresentedSpan = Math.max(1, ...answered.map((trial) => trial.sequence.length));
  const weightedCorrect = answered.reduce((sum, trial) => sum + (trial.correct ? trial.sequence.length : 0), 0);
  const presentedUnits = answered.reduce((sum, trial) => sum + Math.max(trial.sequence.length, 1), 0);
  const memoryScore = activityId === 'color-sequence-drum'
    ? (weightedCorrect / Math.max(presentedUnits, 1)) * 70 + (maxSpan / maxPresentedSpan) * 30
    : (weightedCorrect / Math.max(presentedUnits, 1)) * 80 + (maxSpan / maxPresentedSpan) * 20;
  const fullCreditLimit = Math.max(300, profile.speedReference * 1.5);
  const zeroCreditLimit = Math.min(maxAllowedResponse, fullCreditLimit * 2.5);
  const rawSpeedScore = median <= fullCreditLimit ? 100 : clamp(100 - ((median - fullCreditLimit) / Math.max(zeroCreditLimit - fullCreditLimit, 1)) * 100);
  const speedScore = accuracyRate >= 80 ? rawSpeedScore : Math.min(rawSpeedScore, accuracyRate + 20);
  const attentionScore = clamp(stabilityScore * 0.55 + responseCompletionRate * 0.2 + accuracyRate * 0.25);
  const errorLoad = sequenceErrors + extraErrors * 1.25 + timedOut + perseverativeErrors * 1.35;
  const metrics: MetricScores = {
    accuracy: clamp(accuracyRate),
    memory: clamp(memoryScore),
    speed: clamp(speedScore),
    consistency: attentionScore,
    control: clamp(100 - (errorLoad / total) * 100),
  };
  return {
    metrics,
    total,
    correct,
    median,
    maxSpan,
    errorCount: sequenceErrors + extraErrors + timedOut + perseverativeErrors,
    validResponseCount: correctResponseTimes.length,
    variabilityRatio,
    scoreMethod: 'research-informed-preview-v3',
  };
}

export interface CognitiveFeedback {
  id: string;
  narrative: string;
  strongest: MetricKey;
  needsSupport: MetricKey;
  combinationCapacity: number;
}

export function createProfessionalFeedback(metrics: MetricScores, seedParts: (string | number)[] = [], usedFeedbackIds: string[] = [], language = 'ko', scope: 'session' | 'integrated' = 'session'): CognitiveFeedback {
  const ordered = [...METRIC_KEYS].sort((left, right) => metrics[right] - metrics[left]);
  const strongest = ordered[0];
  const needsSupport = ordered[ordered.length - 1];
  const secondary = ordered[1];
  const baseSeed = hash([...seedParts, ...METRIC_KEYS.map((key) => `${key}:${band(metrics[key])}`)]);
  const seen = new Set(usedFeedbackIds);
  let seed = baseSeed;
  let id = `expert-${seed.toString(36)}-${strongest}-${needsSupport}`;
  for (let offset = 1; seen.has(id) && offset < 62500; offset += 1) {
    seed = (baseSeed + offset * 7919) >>> 0;
    id = `expert-${seed.toString(36)}-${strongest}-${needsSupport}`;
  }
  const isIntegrated = scope === 'integrated';
  const openers = isIntegrated ? INTEGRATED_OPENERS : OPENERS;
  const closers = isIntegrated ? INTEGRATED_CLOSERS : CLOSERS;
  const phraseTable = isIntegrated ? INTEGRATED_PHRASES : PROFESSIONAL_PHRASES;
  const summaryEn = isIntegrated ? PROFESSIONAL_SUMMARY_INTEGRATED_EN : PROFESSIONAL_SUMMARY_EN;
  const narrative = language === 'en'
    ? (isIntegrated
      ? `Cumulative records across sessions indicate that ${summaryEn[strongest][band(metrics[strongest])]}. In contrast, ${summaryEn[needsSupport][band(metrics[needsSupport])]}. In addition, ${summaryEn[secondary][band(metrics[secondary])]}. This interpretation is based on scores averaged across sessions and does not reflect a computed relationship between item difficulty and accuracy.`
      : `Performance logs indicate that ${summaryEn[strongest][band(metrics[strongest])]}. In contrast, ${summaryEn[needsSupport][band(metrics[needsSupport])]}. In addition, ${summaryEn[secondary][band(metrics[secondary])]}. This interpretation summarizes response patterns recorded during this activity.`)
    : `${openers[seed % openers.length]} ${phraseFor(strongest, metrics[strongest], seed, 3, phraseTable)}. ${CONNECTORS[Math.floor(seed / 11) % CONNECTORS.length]} ${phraseFor(needsSupport, metrics[needsSupport], seed, 7, phraseTable)}. ${phraseFor(secondary, metrics[secondary], seed, 11, phraseTable)}. ${closers[Math.floor(seed / 29) % closers.length]}`;
  return { id, narrative, strongest, needsSupport, combinationCapacity: 62500 };
}

export interface CognitiveEasyFeedback {
  title: string;
  body: string;
  strength: string;
  support: string;
}

export function createEasyFeedback(metrics: MetricScores, language = 'ko'): CognitiveEasyFeedback {
  const ordered = [...METRIC_KEYS].sort((left, right) => metrics[right] - metrics[left]);
  const strongest = ordered[0];
  const needsSupport = ordered[ordered.length - 1];
  const level = band(Math.round(METRIC_KEYS.reduce((sum, key) => sum + metrics[key], 0) / METRIC_KEYS.length));
  if (language === 'en') {
    return {
      ...EASY_FEEDBACK_EN[level],
      strength: EASY_STRENGTH_EN[strongest],
      support: metrics[needsSupport] < 68 ? EASY_SUPPORT_EN[needsSupport] : 'Keep enjoying each activity one step at a time.',
    };
  }
  return {
    ...EASY_FEEDBACK[level],
    strength: EASY_STRENGTH[strongest],
    support: metrics[needsSupport] < 68 ? EASY_SUPPORT[needsSupport] : '지금처럼 한 문제씩 즐겁게 이어가 보세요.',
  };
}

export interface CognitiveReport {
  id: string;
  activityId: string;
  activityVersion: string;
  ageProfileId: string;
  inputMode: string;
  createdAt: string;
  metrics: MetricScores;
  overall: number;
  percentile: null;
  performance: ActivityMetricsResult;
  comparison: { key: MetricKey; change: number } | null;
  easyFeedback: CognitiveEasyFeedback;
  feedback: CognitiveFeedback;
  referenceStatus: string;
}

export function createCognitiveReport({ activityId, activityVersion, ageProfile, inputMode, trials, previousReport, feedbackHistory = [], language = 'ko' }: {
  activityId: string;
  activityVersion: string;
  ageProfile: CognitiveScoreProfile;
  inputMode: string;
  trials: CognitiveTrial[];
  previousReport?: CognitiveReport | null;
  feedbackHistory?: string[];
  language?: string;
}): CognitiveReport {
  const result = calculateActivityMetrics({ trials, profile: ageProfile, activityId });
  const overall = clamp(METRIC_KEYS.reduce((sum, key) => sum + result.metrics[key], 0) / METRIC_KEYS.length);
  const comparison = previousReport?.metrics && previousReport.activityVersion === activityVersion
    ? METRIC_KEYS.map((key) => ({ key, change: result.metrics[key] - previousReport.metrics[key] })).sort((a, b) => Math.abs(b.change) - Math.abs(a.change))[0]
    : null;
  const feedback = createProfessionalFeedback(result.metrics, [activityId, activityVersion, ageProfile.id, inputMode, result.total, result.correct, result.maxSpan], feedbackHistory, language);
  const easyFeedback = createEasyFeedback(result.metrics, language);
  return {
    id: `report-${Date.now()}`,
    activityId,
    activityVersion,
    ageProfileId: ageProfile.id,
    inputMode,
    createdAt: new Date().toISOString(),
    metrics: result.metrics,
    overall,
    percentile: null,
    performance: result,
    comparison,
    easyFeedback,
    feedback,
    referenceStatus: 'research-informed-preview',
  };
}
